import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import z from 'zod'
import { ErrorBox } from '@/components/commons/error-box'
import { AlertTriangleIcon } from '@/components/ui/icons'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { FinalScoreBadge } from '@/features/luxe-submissions/components/final-score-badge'
import { ListingDetailsHeaderBase } from '@/features/luxe-submissions/components/listing-details-header-base'
import { PinnedEscalationNote } from '@/features/luxe-submissions/components/pinned-escalation-note'
import { ScoreFormGallery } from '@/features/luxe-submissions/components/score-form-gallery'
import { ScoreFormListingDetails } from '@/features/luxe-submissions/components/score-form-listing-details'
import { ScoreFormLoading } from '@/features/luxe-submissions/components/score-form-loading'
import { ScoreFormPanel } from '@/features/luxe-submissions/components/score-form-panel'
import { useLuxeFeatureFlag } from '@/features/luxe-submissions/queries/use-get-luxe-feature-flags'
import { useGetMMSubmissionDetails } from '@/features/luxe-submissions/queries/use-get-mm-submission-details'
import type { ScoreFormData } from '@/features/luxe-submissions/types'
import {
	findEscalationEntry,
	findSecondReviewEntry
} from '@/features/luxe-submissions/utils'
import { raise } from '@/helpers/utils'
import { useURLParsedParams } from '@/hooks/use-url-parsed-params'
import { routes } from '@/routes'
import { useTranslation } from '@/translations/i18n'

function LuxeSubmissionsMMOverviewParkingLotDetails() {
	const { t } = useTranslation()
	const urlParams = useURLParsedParams(
		z.object({
			id: z.string()
		})
	)

	const listingId = urlParams.success
		? String(urlParams.data.id)
		: raise('Invalid listing ID in URL parameters')

	const { data, isLoading, isError } = useGetMMSubmissionDetails({
		listingId
	})

	const listingDetails = data?.data

	const { isEnabled: isPinnedEscalationNoteEnabled } = useLuxeFeatureFlag(
		'luxe-vv2-scorecard-final-review-comments'
	)

	const methods = useForm<ScoreFormData>()

	useEffect(() => {
		if (listingDetails) {
			methods.reset({
				answers: Object.fromEntries(
					(listingDetails?.answers ?? [])
						.filter((a) => a.answer != null)
						.map((a) => [a.questionId, a.answer as string])
				)
			})
		}
	}, [listingDetails, methods])

	if (isLoading) {
		return <ScoreFormLoading />
	}
	if (!listingDetails || isError) {
		return (
			<ErrorBox
				icon={<AlertTriangleIcon />}
				title={t('errors:default')}
			/>
		)
	}
	const facilities = [
		{ keyName: 'guests', count: listingDetails?.maxGuests },
		{ keyName: 'bedrooms', count: listingDetails?.bedrooms },
		{ keyName: 'beds', count: listingDetails?.beds },
		{ keyName: 'baths', count: listingDetails?.bathrooms }
	].filter((item) => item.count != null) as {
		keyName: 'bedrooms' | 'beds' | 'baths' | 'guests'
		count: number
	}[]
	// No separate escalationNote here: the note comes with the entry itself
	const escalationDetails = findEscalationEntry(
		listingDetails.finalReviewComments
	)
	const secondReviewDetails = findSecondReviewEntry(
		listingDetails.finalReviewComments
	)

	return (
		<div className='flex flex-1 flex-col overflow-hidden'>
			<FormProvider {...methods}>
				{/* Header */}
				<ListingDetailsHeaderBase
					backUrl={
						routes.LUXE_SUBMISSIONS_MM_PARKING_LOT.handle.fullPath
					}
					inventory={listingDetails?.listingInventory}
					listingId={
						listingDetails?.airbnbListingId ?? listingDetails?.id
					}
					listingLink={listingDetails?.listingUrl}
					location={listingDetails?.location}
					score={
						// The breakdown is pinned over the scorecard below
						<FinalScoreBadge
							blockersFailedCount={
								listingDetails.blockersFailedCount
							}
							designScore={listingDetails.score}
							finalScore={listingDetails.finalScore}
							hasBreakdownTooltip={false}
							hasScoreFallback={false}
						/>
					}
					title={t('luxe_submissions:listing_submission_for')}
				/>

				{/* Main content */}
				<div className='grid grid-cols-[1.5fr_2fr] overflow-hidden'>
					<ScrollArea
						className='h-full'
						scrollbar={<Scrollbar />}
						style={{ overflow: 'hidden' }}
					>
						<div className='border-neutral-200 border-r'>
							{/* The score pins on its own; the notes stay behind
								luxe-vv2-scorecard-final-review-comments */}
							<PinnedEscalationNote
								blockersFailedCount={
									listingDetails.blockersFailedCount
								}
								designScore={listingDetails.score}
								details={escalationDetails}
								note={
									isPinnedEscalationNoteEnabled
										? escalationDetails?.note
										: null
								}
								secondReviewDetails={secondReviewDetails}
								secondReviewNote={
									isPinnedEscalationNoteEnabled
										? secondReviewDetails?.note
										: null
								}
							/>
							<ScoreFormPanel
								formDisabled={true}
								mmViewOnly={true}
								questions={listingDetails.questions ?? []}
							/>
						</div>
					</ScrollArea>

					<div className='flex flex-col gap-8 overflow-auto px-6'>
						<ScoreFormGallery
							listingId={
								listingDetails?.airbnbListingId ??
								listingDetails?.id ??
								''
							}
							photos={listingDetails?.images ?? []}
							repositoryUrl={listingDetails?.photoRepositoryUrl}
						/>
						<ScoreFormListingDetails facilities={facilities} />
					</div>
				</div>
			</FormProvider>
		</div>
	)
}

export { LuxeSubmissionsMMOverviewParkingLotDetails }
